import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { listProducts, listCategories, getReviewsSummary } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import ProductCard from "@/components/ProductCard";

export default function Shop() {
  const { t, lang } = useLanguage();
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "all";

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ratings, setRatings] = useState({});
  const [loading, setLoading] = useState(true);

  // Persist ?code=... from launch banner so cart can auto-apply it later
  useEffect(() => {
    const code = params.get("code");
    if (code) localStorage.setItem("pendingDiscountCode", code);
  }, [params]);

  useEffect(() => {
    listCategories().then((r) => setCategories(r.categories)).catch(() => {});
    getReviewsSummary().then(setRatings).catch(() => setRatings({}));
  }, []);

  useEffect(() => {
    setLoading(true);
    listProducts({ category })
      .then(setProducts)
      .finally(() => setLoading(false));
  }, [category]);

  const setCategory = (slug) => {
    if (slug === "all") setParams({});
    else setParams({ category: slug });
  };

  return (
    <div>
      {/* Header */}
      <section className="bg-[#F9F8F6] border-b border-[#E5E2DC]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-20">
          <div className="flex items-center gap-3 mb-5">
            <span className="w-8 h-px bg-[#D97B66]"></span>
            <span className="text-xs uppercase tracking-[0.25em] text-[#6B726C] font-medium">
              {t("nav_shop")}
            </span>
          </div>
          <h1 className="font-heading text-4xl md:text-6xl tracking-tight font-light text-[#2C302E]">
            {lang === "en" ? "All resources" : "Alle ressurser"}
          </h1>
        </div>
      </section>

      {/* Filters */}
      <div className="border-b border-[#E5E2DC] bg-white sticky top-20 z-30 backdrop-blur-xl bg-white/80">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-5 overflow-x-auto">
          <div className="flex items-center gap-2 md:gap-3 min-w-max">
            <button
              data-testid="filter-all"
              onClick={() => setCategory("all")}
              className={`px-4 md:px-5 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                category === "all"
                  ? "bg-[#2C302E] text-white"
                  : "border border-[#E5E2DC] text-[#2C302E] hover:border-[#2C302E]"
              }`}
            >
              {t("cat_all")}
            </button>
            {categories.map((c) => (
              <button
                key={c.slug}
                data-testid={`filter-${c.slug}`}
                onClick={() => setCategory(c.slug)}
                className={`px-4 md:px-5 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  category === c.slug
                    ? "bg-[#2C302E] text-white"
                    : "border border-[#E5E2DC] text-[#2C302E] hover:border-[#2C302E]"
                }`}
              >
                {lang === "en" ? c.name_en : c.name_no}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16 md:py-24">
        {loading ? (
          <div className="text-[#6B726C] text-center py-24">{t("loading")}</div>
        ) : products.length === 0 ? (
          <div className="text-[#6B726C] text-center py-24">
            {lang === "en" ? "No products found." : "Ingen produkter funnet."}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {products.map((p, i) => (
              <ProductCard
                key={p.id}
                product={p}
                idx={i}
                rating={ratings[p.slug] || null}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
